import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Plus, Trash2, RefreshCw, CheckCircle2, AlertCircle, X } from "lucide-react";

// Phase 1A.3 — minimal admin UI for the Finance Foundation collections.
// Reuses pattern of `InspectorPayrollReportModule.jsx`: each module is a
// self-contained component, plain Tailwind, axios, no shared util refactors.

const API = `${process.env.REACT_APP_BACKEND_URL}/api/finance/foundation`;
axios.defaults.withCredentials = true;


const $$ = (n) => `$${(Number(n) || 0).toFixed(2)}`;


const StatusBadge = ({ status }) => {
  const map = {
    pending:  "bg-amber-100 text-amber-800",
    draft:    "bg-gray-100 text-gray-700",
    approved: "bg-blue-100 text-blue-800",
    paid:     "bg-emerald-100 text-emerald-800",
    void:     "bg-red-100 text-red-700 line-through",
  };
  const cls = map[status] || "bg-gray-100 text-gray-700";
  return (
    <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${cls}`}>
      {status || "—"}
    </span>
  );
};


const Toast = ({ msg, onClose }) => {
  if (!msg) return null;
  const cls = msg.type === "ok"
    ? "bg-emerald-50 border-emerald-300 text-emerald-800"
    : "bg-red-50 border-red-300 text-red-800";
  return (
    <div className={`px-3 py-2 rounded-lg border ${cls} text-xs flex items-center gap-2`} data-testid="finance-foundation-toast">
      {msg.type === "ok" ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertCircle className="h-3.5 w-3.5" />}
      <span className="flex-1">{msg.text}</span>
      <button onClick={onClose} className="text-current opacity-60 hover:opacity-100"><X className="h-3 w-3" /></button>
    </div>
  );
};


/* ─────────────────────────────── Expense Categories ─────────────────────────────── */

export const ExpenseCategoriesModule = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [form, setForm] = useState({ id: "", label: "", kind: "operational", parent_id: "" });
  const [includeInactive, setIncludeInactive] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/expense-categories`, { params: { include_inactive: includeInactive } });
      setItems(r.data?.categories || []);
    } catch (e) {
      setMsg({ type: "err", text: e.response?.data?.detail || "Failed to load categories" });
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e?.preventDefault();
    setMsg(null);
    try {
      const payload = { ...form };
      if (!payload.parent_id) delete payload.parent_id;
      await axios.post(`${API}/expense-categories`, payload);
      setMsg({ type: "ok", text: `Category "${form.label}" created` });
      setForm({ id: "", label: "", kind: "operational", parent_id: "" });
      load();
    } catch (e) {
      setMsg({ type: "err", text: e.response?.data?.detail || "Failed to create" });
    }
  };

  const seed = async () => {
    if (!window.confirm("Seed default expense categories? This is idempotent.")) return;
    try {
      const r = await axios.post(`${API}/expense-categories/seed`);
      setMsg({ type: "ok", text: `Seed done — inserted ${r.data.inserted}, skipped ${r.data.skipped}` });
      load();
    } catch (e) {
      setMsg({ type: "err", text: e.response?.data?.detail || "Seed failed" });
    }
  };

  const deactivate = async (cat_id) => {
    if (!window.confirm("Deactivate this category?")) return;
    try {
      await axios.delete(`${API}/expense-categories/${cat_id}`);
      setMsg({ type: "ok", text: "Category deactivated" });
      load();
    } catch (e) {
      setMsg({ type: "err", text: e.response?.data?.detail || "Failed" });
    }
  };

  return (
    <div className="space-y-3" data-testid="expense-categories-module">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-bold text-gray-800">Expense Categories</h2>
        <div className="flex items-center gap-2">
          <label className="text-[11px] text-gray-500 flex items-center gap-1">
            <input type="checkbox" checked={includeInactive} onChange={e => setIncludeInactive(e.target.checked)} data-testid="categories-show-inactive" />
            show inactive
          </label>
          <button onClick={seed} className="text-xs font-semibold px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-700" data-testid="categories-seed-btn">Seed defaults</button>
          <button onClick={load} className="text-xs flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-100" data-testid="categories-refresh-btn">
            <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      <Toast msg={msg} onClose={() => setMsg(null)} />

      <form onSubmit={submit} className="bg-white p-3 rounded-xl border border-gray-200 grid grid-cols-2 md:grid-cols-5 gap-2 items-end">
        <input value={form.id} onChange={e => setForm({ ...form, id: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} placeholder="slug (e.g. fuel)" required className="px-2 py-1.5 border border-gray-200 rounded text-xs" data-testid="cat-form-id" />
        <input value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} placeholder="Label" required className="px-2 py-1.5 border border-gray-200 rounded text-xs col-span-2" data-testid="cat-form-label" />
        <select value={form.kind} onChange={e => setForm({ ...form, kind: e.target.value })} className="px-2 py-1.5 border border-gray-200 rounded text-xs" data-testid="cat-form-kind">
          {["fixed","variable","payroll","transmission","operational"].map(k => <option key={k} value={k}>{k}</option>)}
        </select>
        <button type="submit" className="bg-[#162d4a] text-white text-xs font-semibold py-1.5 px-3 rounded flex items-center gap-1 hover:bg-[#1F4068]" data-testid="cat-form-submit">
          <Plus className="h-3 w-3" /> Create
        </button>
      </form>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="min-w-full text-xs">
          <thead className="bg-gray-50 text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left">ID</th>
              <th className="px-3 py-2 text-left">Label</th>
              <th className="px-3 py-2 text-left">Kind</th>
              <th className="px-3 py-2 text-left">Active</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.map(c => (
              <tr key={c.id} className="border-t border-gray-100" data-testid={`cat-row-${c.id}`}>
                <td className="px-3 py-1.5 font-mono text-[11px]">{c.id}</td>
                <td className="px-3 py-1.5 font-semibold">{c.label}</td>
                <td className="px-3 py-1.5">{c.kind}</td>
                <td className="px-3 py-1.5">{c.active ? <span className="text-emerald-600 font-bold">yes</span> : <span className="text-gray-400">no</span>}</td>
                <td className="px-3 py-1.5 text-right">
                  {c.active && (
                    <button onClick={() => deactivate(c.id)} className="text-red-600 hover:text-red-800" data-testid={`cat-del-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  )}
                </td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-gray-400">No categories. Click "Seed defaults" to load the standard 12.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};


/* ─────────────────────────────── Vendors ─────────────────────────────── */

export const VendorsModule = () => {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [form, setForm] = useState({
    name: "", contact_name: "", contact_phone: "", contact_email: "",
    tax_id: "", payment_terms_days: 0, default_category_id: "", notes: "",
  });
  const [editing, setEditing] = useState(null); // {id, ...form}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [v, c] = await Promise.all([
        axios.get(`${API}/vendors`),
        axios.get(`${API}/expense-categories`),
      ]);
      setItems(v.data?.vendors || []);
      setCats(c.data?.categories || []);
    } catch (e) {
      setMsg({ type: "err", text: e.response?.data?.detail || "Failed to load" });
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e?.preventDefault();
    setMsg(null);
    try {
      const payload = { ...form, payment_terms_days: Number(form.payment_terms_days) || 0 };
      if (!payload.default_category_id) delete payload.default_category_id;
      if (editing) {
        await axios.patch(`${API}/vendors/${editing.id}`, payload);
        setMsg({ type: "ok", text: "Vendor updated" });
        setEditing(null);
      } else {
        await axios.post(`${API}/vendors`, payload);
        setMsg({ type: "ok", text: `Vendor "${form.name}" created` });
      }
      setForm({ name: "", contact_name: "", contact_phone: "", contact_email: "", tax_id: "", payment_terms_days: 0, default_category_id: "", notes: "" });
      load();
    } catch (e) {
      setMsg({ type: "err", text: e.response?.data?.detail || "Failed" });
    }
  };

  const startEdit = (v) => {
    setEditing(v);
    setForm({
      name: v.name || "", contact_name: v.contact_name || "", contact_phone: v.contact_phone || "",
      contact_email: v.contact_email || "", tax_id: v.tax_id || "",
      payment_terms_days: v.payment_terms_days || 0,
      default_category_id: v.default_category_id || "", notes: v.notes || "",
    });
  };

  const cancelEdit = () => {
    setEditing(null);
    setForm({ name: "", contact_name: "", contact_phone: "", contact_email: "", tax_id: "", payment_terms_days: 0, default_category_id: "", notes: "" });
  };

  const deactivate = async (id) => {
    if (!window.confirm("Deactivate this vendor?")) return;
    try {
      await axios.delete(`${API}/vendors/${id}`);
      setMsg({ type: "ok", text: "Vendor deactivated" });
      load();
    } catch (e) {
      setMsg({ type: "err", text: e.response?.data?.detail || "Failed" });
    }
  };

  return (
    <div className="space-y-3" data-testid="vendors-module">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-bold text-gray-800">Vendors {editing && <span className="text-amber-700 text-[11px] ml-2">· editing {editing.name}</span>}</h2>
        <button onClick={load} className="text-xs flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-100" data-testid="vendors-refresh-btn">
          <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <Toast msg={msg} onClose={() => setMsg(null)} />

      <form onSubmit={submit} className="bg-white p-3 rounded-xl border border-gray-200 grid grid-cols-2 md:grid-cols-4 gap-2">
        <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Vendor name" required className="px-2 py-1.5 border border-gray-200 rounded text-xs col-span-2" data-testid="vendor-form-name" />
        <input value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} placeholder="Contact name" className="px-2 py-1.5 border border-gray-200 rounded text-xs" data-testid="vendor-form-contact" />
        <input value={form.contact_phone} onChange={e => setForm({ ...form, contact_phone: e.target.value })} placeholder="Phone" className="px-2 py-1.5 border border-gray-200 rounded text-xs" data-testid="vendor-form-phone" />
        <input value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })} placeholder="Email" type="email" className="px-2 py-1.5 border border-gray-200 rounded text-xs col-span-2" data-testid="vendor-form-email" />
        <input value={form.tax_id} onChange={e => setForm({ ...form, tax_id: e.target.value })} placeholder="Tax ID" className="px-2 py-1.5 border border-gray-200 rounded text-xs" data-testid="vendor-form-tax" />
        <input value={form.payment_terms_days} onChange={e => setForm({ ...form, payment_terms_days: e.target.value })} placeholder="Terms (days)" type="number" min="0" className="px-2 py-1.5 border border-gray-200 rounded text-xs" data-testid="vendor-form-terms" />
        <select value={form.default_category_id} onChange={e => setForm({ ...form, default_category_id: e.target.value })} className="px-2 py-1.5 border border-gray-200 rounded text-xs col-span-2" data-testid="vendor-form-cat">
          <option value="">Default category (optional)</option>
          {cats.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Notes" className="px-2 py-1.5 border border-gray-200 rounded text-xs col-span-2" data-testid="vendor-form-notes" />
        <div className="col-span-4 flex justify-end gap-2">
          {editing && <button type="button" onClick={cancelEdit} className="text-xs px-3 py-1.5 rounded border border-gray-300 hover:bg-gray-50" data-testid="vendor-form-cancel">Cancel</button>}
          <button type="submit" className="bg-[#162d4a] text-white text-xs font-semibold py-1.5 px-4 rounded hover:bg-[#1F4068] flex items-center gap-1" data-testid="vendor-form-submit">
            <Plus className="h-3 w-3" /> {editing ? "Save changes" : "Create vendor"}
          </button>
        </div>
      </form>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="min-w-full text-xs">
          <thead className="bg-gray-50 text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left">Name</th>
              <th className="px-3 py-2 text-left">Contact</th>
              <th className="px-3 py-2 text-left">Terms</th>
              <th className="px-3 py-2 text-left">Default Category</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.map(v => (
              <tr key={v.id} className="border-t border-gray-100" data-testid={`vendor-row-${v.id}`}>
                <td className="px-3 py-1.5 font-semibold">{v.name}</td>
                <td className="px-3 py-1.5 text-gray-600">
                  {v.contact_name && <div>{v.contact_name}</div>}
                  {v.contact_phone && <div className="text-[10px]">{v.contact_phone}</div>}
                  {v.contact_email && <div className="text-[10px]">{v.contact_email}</div>}
                </td>
                <td className="px-3 py-1.5">{v.payment_terms_days || 0}d</td>
                <td className="px-3 py-1.5">{cats.find(c => c.id === v.default_category_id)?.label || "—"}</td>
                <td className="px-3 py-1.5 text-right space-x-2">
                  <button onClick={() => startEdit(v)} className="text-blue-600 hover:underline text-[11px]" data-testid={`vendor-edit-${v.id}`}>Edit</button>
                  <button onClick={() => deactivate(v.id)} className="text-red-600 hover:text-red-800" data-testid={`vendor-del-${v.id}`}><Trash2 className="h-3.5 w-3.5 inline" /></button>
                </td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-gray-400">No vendors yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};


/* ─────────────────────────────── Bills ─────────────────────────────── */

export const BillsModule = () => {
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [filter, setFilter] = useState({ status: "", vendor_id: "", category_id: "" });
  const [form, setForm] = useState({
    bill_number: "", vendor_id: "", expense_category_id: "",
    amount: "", due_date: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
    period_start: "", period_end: "", notes: "",
  });
  const [payingBill, setPayingBill] = useState(null);
  const [payForm, setPayForm] = useState({ payment_method: "ach", payment_reference: "", account: "main" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.status) params.status = filter.status;
      if (filter.vendor_id) params.vendor_id = filter.vendor_id;
      if (filter.category_id) params.category_id = filter.category_id;
      const [b, v, c] = await Promise.all([
        axios.get(`${API}/bills`, { params }),
        axios.get(`${API}/vendors`),
        axios.get(`${API}/expense-categories`),
      ]);
      setItems(b.data?.bills || []);
      setVendors(v.data?.vendors || []);
      setCats(c.data?.categories || []);
    } catch (e) {
      setMsg({ type: "err", text: e.response?.data?.detail || "Failed to load" });
    } finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e?.preventDefault();
    setMsg(null);
    try {
      const payload = { ...form, amount: Number(form.amount) };
      if (!payload.period_start) delete payload.period_start;
      if (!payload.period_end) delete payload.period_end;
      if (!payload.vendor_id) delete payload.vendor_id;
      const sel = vendors.find(v => v.id === form.vendor_id);
      if (sel) payload.vendor_name = sel.name;
      await axios.post(`${API}/bills`, payload);
      setMsg({ type: "ok", text: `Bill ${form.bill_number || "(no number)"} created` });
      setForm({ ...form, bill_number: "", amount: "", notes: "" });
      load();
    } catch (e) {
      setMsg({ type: "err", text: e.response?.data?.detail || "Failed" });
    }
  };

  const startPay = (b) => { setPayingBill(b); setPayForm({ payment_method: "ach", payment_reference: "", account: "main" }); };

  const submitPay = async (e) => {
    e?.preventDefault();
    if (!payingBill) return;
    try {
      await axios.post(`${API}/bills/${payingBill.id}/pay`, payForm);
      setMsg({ type: "ok", text: `Bill ${payingBill.bill_number || payingBill.id} marked paid · cash_movement created` });
      setPayingBill(null);
      load();
    } catch (e) {
      setMsg({ type: "err", text: e.response?.data?.detail || "Pay failed" });
    }
  };

  const voidBill = async (id) => {
    if (!window.confirm("Void this bill?")) return;
    try {
      await axios.delete(`${API}/bills/${id}`);
      setMsg({ type: "ok", text: "Bill voided" });
      load();
    } catch (e) {
      setMsg({ type: "err", text: e.response?.data?.detail || "Failed" });
    }
  };

  return (
    <div className="space-y-3" data-testid="bills-module">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-sm font-bold text-gray-800">Bills</h2>
        <div className="flex items-center gap-2">
          <select value={filter.status} onChange={e => setFilter({ ...filter, status: e.target.value })} className="text-xs px-2 py-1 border border-gray-200 rounded" data-testid="bills-filter-status">
            <option value="">All statuses</option>
            {["pending","draft","approved","paid","void"].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={filter.vendor_id} onChange={e => setFilter({ ...filter, vendor_id: e.target.value })} className="text-xs px-2 py-1 border border-gray-200 rounded" data-testid="bills-filter-vendor">
            <option value="">All vendors</option>
            {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
          <button onClick={load} className="text-xs flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-100" data-testid="bills-refresh-btn">
            <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      <Toast msg={msg} onClose={() => setMsg(null)} />

      <form onSubmit={submit} className="bg-white p-3 rounded-xl border border-gray-200 grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
        <input value={form.bill_number} onChange={e => setForm({ ...form, bill_number: e.target.value })} placeholder="Bill #" className="px-2 py-1.5 border border-gray-200 rounded text-xs" data-testid="bill-form-number" />
        <select value={form.vendor_id} onChange={e => setForm({ ...form, vendor_id: e.target.value })} className="px-2 py-1.5 border border-gray-200 rounded text-xs col-span-2" data-testid="bill-form-vendor">
          <option value="">Vendor (optional for payroll)</option>
          {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
        <select value={form.expense_category_id} onChange={e => setForm({ ...form, expense_category_id: e.target.value })} required className="px-2 py-1.5 border border-gray-200 rounded text-xs" data-testid="bill-form-category">
          <option value="">Category *</option>
          {cats.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <input value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} placeholder="Amount $ *" type="number" step="0.01" min="0.01" required className="px-2 py-1.5 border border-gray-200 rounded text-xs" data-testid="bill-form-amount" />
        <input value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} type="date" required className="px-2 py-1.5 border border-gray-200 rounded text-xs" data-testid="bill-form-due" />
        <input value={form.period_start} onChange={e => setForm({ ...form, period_start: e.target.value })} type="date" placeholder="Period start" className="px-2 py-1.5 border border-gray-200 rounded text-xs" data-testid="bill-form-pstart" />
        <input value={form.period_end} onChange={e => setForm({ ...form, period_end: e.target.value })} type="date" placeholder="Period end" className="px-2 py-1.5 border border-gray-200 rounded text-xs" data-testid="bill-form-pend" />
        <input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Notes" className="px-2 py-1.5 border border-gray-200 rounded text-xs col-span-3" data-testid="bill-form-notes" />
        <button type="submit" className="bg-[#162d4a] text-white text-xs font-semibold py-1.5 px-4 rounded hover:bg-[#1F4068] flex items-center gap-1" data-testid="bill-form-submit">
          <Plus className="h-3 w-3" /> Create bill
        </button>
      </form>

      {payingBill && (
        <form onSubmit={submitPay} className="bg-emerald-50 border border-emerald-300 p-3 rounded-xl" data-testid="bill-pay-form">
          <div className="text-xs font-bold text-emerald-900 mb-2">Mark as Paid · {payingBill.bill_number || payingBill.id} · {$$(payingBill.amount)}</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 items-end">
            <select value={payForm.payment_method} onChange={e => setPayForm({ ...payForm, payment_method: e.target.value })} className="px-2 py-1.5 border border-gray-200 rounded text-xs" data-testid="bill-pay-method">
              {["ach","wire","check","cash","card","zelle"].map(m => <option key={m} value={m}>{m.toUpperCase()}</option>)}
            </select>
            <input value={payForm.payment_reference} onChange={e => setPayForm({ ...payForm, payment_reference: e.target.value })} placeholder="Reference / check #" className="px-2 py-1.5 border border-gray-200 rounded text-xs col-span-2" data-testid="bill-pay-ref" />
            <select value={payForm.account} onChange={e => setPayForm({ ...payForm, account: e.target.value })} className="px-2 py-1.5 border border-gray-200 rounded text-xs" data-testid="bill-pay-account">
              {["main","reserve","cash"].map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            <div className="flex gap-1">
              <button type="button" onClick={() => setPayingBill(null)} className="text-xs px-2 py-1.5 border border-gray-300 rounded hover:bg-white" data-testid="bill-pay-cancel">Cancel</button>
              <button type="submit" className="bg-emerald-600 text-white text-xs font-semibold py-1.5 px-3 rounded hover:bg-emerald-700" data-testid="bill-pay-confirm">Confirm Pay</button>
            </div>
          </div>
        </form>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="min-w-full text-xs">
          <thead className="bg-gray-50 text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left">Bill #</th>
              <th className="px-3 py-2 text-left">Vendor</th>
              <th className="px-3 py-2 text-left">Category</th>
              <th className="px-3 py-2 text-right">Amount</th>
              <th className="px-3 py-2 text-left">Due</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.map(b => (
              <tr key={b.id} className="border-t border-gray-100" data-testid={`bill-row-${b.id}`}>
                <td className="px-3 py-1.5 font-mono text-[11px]">{b.bill_number || "—"}</td>
                <td className="px-3 py-1.5">{b.vendor_name || "—"}</td>
                <td className="px-3 py-1.5">{b.category_label || b.expense_category_id}</td>
                <td className="px-3 py-1.5 text-right font-semibold">{$$(b.amount)}</td>
                <td className="px-3 py-1.5">{b.due_date}</td>
                <td className="px-3 py-1.5"><StatusBadge status={b.status} /></td>
                <td className="px-3 py-1.5 text-right space-x-2">
                  {b.status !== "paid" && b.status !== "void" && (
                    <button onClick={() => startPay(b)} className="text-emerald-700 hover:underline text-[11px] font-semibold" data-testid={`bill-pay-${b.id}`}>Mark Paid</button>
                  )}
                  {b.status !== "paid" && b.status !== "void" && (
                    <button onClick={() => voidBill(b.id)} className="text-red-600 hover:text-red-800" data-testid={`bill-void-${b.id}`}><Trash2 className="h-3.5 w-3.5 inline" /></button>
                  )}
                </td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-400">No bills.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};


/* ─────────────────────────────── Cash Movements ─────────────────────────────── */

export const CashMovementsModule = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [filter, setFilter] = useState({ from_date: "", to_date: "", direction: "", account: "" });
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    direction: "out", amount: "", source: "other",
    description: "", account: "main",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filter).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await axios.get(`${API}/cash-movements`, { params });
      setItems(r.data?.cash_movements || []);
    } catch (e) {
      setMsg({ type: "err", text: e.response?.data?.detail || "Failed to load" });
    } finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e?.preventDefault();
    setMsg(null);
    try {
      await axios.post(`${API}/cash-movements`, { ...form, amount: Number(form.amount) });
      setMsg({ type: "ok", text: "Cash movement recorded" });
      setForm({ ...form, amount: "", description: "" });
      load();
    } catch (e) {
      setMsg({ type: "err", text: e.response?.data?.detail || "Failed" });
    }
  };

  const remove = async (cm) => {
    if (cm.linked_kind === "bill") {
      window.alert("Cannot delete a bill-linked movement. Void the bill instead.");
      return;
    }
    if (!window.confirm("Delete this cash movement?")) return;
    try {
      await axios.delete(`${API}/cash-movements/${cm.id}`);
      setMsg({ type: "ok", text: "Deleted" });
      load();
    } catch (e) {
      setMsg({ type: "err", text: e.response?.data?.detail || "Failed" });
    }
  };

  const totals = items.reduce((acc, m) => {
    if (m.direction === "in") acc.in += Number(m.amount) || 0;
    else if (m.direction === "out") acc.out += Number(m.amount) || 0;
    return acc;
  }, { in: 0, out: 0 });

  return (
    <div className="space-y-3" data-testid="cash-movements-module">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-sm font-bold text-gray-800">Cash Movements</h2>
        <div className="flex items-center gap-2">
          <input type="date" value={filter.from_date} onChange={e => setFilter({ ...filter, from_date: e.target.value })} className="text-xs px-2 py-1 border border-gray-200 rounded" data-testid="cm-filter-from" />
          <input type="date" value={filter.to_date} onChange={e => setFilter({ ...filter, to_date: e.target.value })} className="text-xs px-2 py-1 border border-gray-200 rounded" data-testid="cm-filter-to" />
          <select value={filter.direction} onChange={e => setFilter({ ...filter, direction: e.target.value })} className="text-xs px-2 py-1 border border-gray-200 rounded" data-testid="cm-filter-direction">
            <option value="">Both</option>
            <option value="in">In</option>
            <option value="out">Out</option>
          </select>
          <button onClick={load} className="text-xs flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-100" data-testid="cm-refresh-btn">
            <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      <Toast msg={msg} onClose={() => setMsg(null)} />

      <div className="grid grid-cols-3 gap-2">
        <div className="bg-emerald-50 border border-emerald-200 px-3 py-2 rounded-xl">
          <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Cash In</div>
          <div className="text-lg font-extrabold text-emerald-700">{$$(totals.in)}</div>
        </div>
        <div className="bg-red-50 border border-red-200 px-3 py-2 rounded-xl">
          <div className="text-[10px] uppercase tracking-wider text-red-700 font-bold">Cash Out</div>
          <div className="text-lg font-extrabold text-red-700">{$$(totals.out)}</div>
        </div>
        <div className="bg-[#162d4a] text-white px-3 py-2 rounded-xl">
          <div className="text-[10px] uppercase tracking-wider opacity-80 font-bold">Net</div>
          <div className="text-lg font-extrabold">{$$(totals.in - totals.out)}</div>
        </div>
      </div>

      <form onSubmit={submit} className="bg-white p-3 rounded-xl border border-gray-200 grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
        <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} required className="px-2 py-1.5 border border-gray-200 rounded text-xs" data-testid="cm-form-date" />
        <select value={form.direction} onChange={e => setForm({ ...form, direction: e.target.value })} className="px-2 py-1.5 border border-gray-200 rounded text-xs" data-testid="cm-form-direction">
          <option value="in">In</option>
          <option value="out">Out</option>
        </select>
        <input value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} type="number" step="0.01" min="0.01" placeholder="Amount $" required className="px-2 py-1.5 border border-gray-200 rounded text-xs" data-testid="cm-form-amount" />
        <select value={form.source} onChange={e => setForm({ ...form, source: e.target.value })} className="px-2 py-1.5 border border-gray-200 rounded text-xs" data-testid="cm-form-source">
          {["check_payment","bill_paid","payroll_paid","adjustment","transfer","other"].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={form.account} onChange={e => setForm({ ...form, account: e.target.value })} className="px-2 py-1.5 border border-gray-200 rounded text-xs" data-testid="cm-form-account">
          {["main","reserve","cash"].map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <button type="submit" className="bg-[#162d4a] text-white text-xs font-semibold py-1.5 px-3 rounded hover:bg-[#1F4068] flex items-center gap-1" data-testid="cm-form-submit">
          <Plus className="h-3 w-3" /> Record
        </button>
        <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Description / memo" className="px-2 py-1.5 border border-gray-200 rounded text-xs col-span-6" data-testid="cm-form-desc" />
      </form>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="min-w-full text-xs">
          <thead className="bg-gray-50 text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left">Date</th>
              <th className="px-3 py-2 text-left">Dir</th>
              <th className="px-3 py-2 text-right">Amount</th>
              <th className="px-3 py-2 text-left">Source</th>
              <th className="px-3 py-2 text-left">Account</th>
              <th className="px-3 py-2 text-left">Description</th>
              <th className="px-3 py-2 text-left">Linked</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.map(m => (
              <tr key={m.id} className="border-t border-gray-100" data-testid={`cm-row-${m.id}`}>
                <td className="px-3 py-1.5">{m.date}</td>
                <td className="px-3 py-1.5">
                  <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${m.direction === "in" ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-700"}`}>{m.direction}</span>
                </td>
                <td className="px-3 py-1.5 text-right font-semibold">{$$(m.amount)}</td>
                <td className="px-3 py-1.5 text-gray-600">{m.source}</td>
                <td className="px-3 py-1.5 text-gray-600">{m.account}</td>
                <td className="px-3 py-1.5 text-gray-700">{m.description || "—"}</td>
                <td className="px-3 py-1.5 text-[10px] text-gray-500">{m.linked_kind ? `${m.linked_kind}:${(m.linked_id || "").slice(-6)}` : "—"}</td>
                <td className="px-3 py-1.5 text-right">
                  {m.linked_kind !== "bill" && (
                    <button onClick={() => remove(m)} className="text-red-600 hover:text-red-800" data-testid={`cm-del-${m.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  )}
                </td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-gray-400">No cash movements in range.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
